/**
 * 統合オプションモーダル管理クラス
 * 複数のオプションを1つのモーダルにまとめて表示・管理
 * 
 * 依存モジュール:
 * - grouped-options-utils.js       - ユーティリティ関数
 * - grouped-options-visibility.js  - 表示制御ルール
 * - grouped-options-ui-generator.js - UI生成
 * - grouped-options-mobile.js      - モバイル用
 */

(function() {
  'use strict';

  // 依存モジュールの参照
  var Utils = window.GroupedOptionsUtils;
  var Visibility = window.GroupedOptionsVisibility;
  var UIGenerator = window.GroupedOptionsUIGenerator;
  var Mobile = window.GroupedOptionsMobile;

  /**
   * GroupedOptionsManager クラス
   * @param {Array} options - オプション配列
   */
  function GroupedOptionsManager(options) {
    this.allOptions = options || [];
    this.requiredOptions = [];
    this.optionalOptions = [];
    this.selectedOptions = new Map();
    this.selectedOptionsBackup = null;
    this.currentCategory = null;
    this.currentModalType = 'required';
    this.colorVisibilityRules = [];
    
    this.init();
  }

  // =========================================
  // 初期化
  // =========================================
  
  GroupedOptionsManager.prototype.init = function() {
    // 表示制御ルールを読み込み
    this.colorVisibilityRules = Visibility.loadVisibilityRules();
    
    // オプションを必須/任意に分類
    this.categorizeOptions();
    
    // モーダルHTML生成
    this.createModal();
    
    // イベントリスナー設定
    this.attachEventListeners();
    
    // サイドナビボタン生成
    this.createSidebarButtons();
  };

  GroupedOptionsManager.prototype.categorizeOptions = function() {
    var self = this;

    // 商品個別の並び順設定があるかチェック
    var customOrder = window.productData && window.productData.option_display_order;
    var useCustomOrder = false;

    // useGlobalがfalse（ブール値または文字列）の場合に個別設定を使用
    var shouldUseCustom = customOrder && typeof customOrder === 'object' &&
      (customOrder.useGlobal === false || customOrder.useGlobal === 'false');

    if (shouldUseCustom) {
      // 商品個別設定を使用
      useCustomOrder = true;

      // 個別設定に基づいてソート
      // ID、handle、labelのいずれかでマッチングできるようにマップを作成
      var orderMap = {};

      if (customOrder.required && Array.isArray(customOrder.required)) {
        customOrder.required.forEach(function(id, index) {
          orderMap[id] = index;
        });
      }
      var requiredCount = (customOrder.required && customOrder.required.length) || 0;
      if (customOrder.optional && Array.isArray(customOrder.optional)) {
        customOrder.optional.forEach(function(id, index) {
          orderMap[id] = requiredCount + index;
        });
      }

      // オプションIDとマッチング（id, handle, labelのいずれかでマッチ）
      var getOptionOrder = function(opt) {
        // 優先順位: id → handle → label
        if (orderMap[opt.id] !== undefined) return orderMap[opt.id];
        if (opt.handle && orderMap[opt.handle] !== undefined) return orderMap[opt.handle];
        if (opt.label && orderMap[opt.label] !== undefined) return orderMap[opt.label];
        return 9999;
      };

      this.allOptions.sort(function(a, b) {
        var orderA = getOptionOrder(a);
        var orderB = getOptionOrder(b);
        return orderA - orderB;
      });
    } else {
      // 共通設定（display_order）でソート
      this.allOptions.sort(function(a, b) {
        var orderA = parseInt(a.display_order) || 9999;
        var orderB = parseInt(b.display_order) || 9999;
        return orderA - orderB;
      });
    }

    
    this.allOptions.forEach(function(option) {
      if (option.required) {
        self.requiredOptions.push(option);
      } else {
        self.optionalOptions.push(option);
      }
    });
    
  };

  // =========================================
  // モーダル操作
  // =========================================

  GroupedOptionsManager.prototype.createModal = function() {
    var self = this;
    var overlay = UIGenerator.createModal(
      function() { return self.generateCategoryList(); },
      function() { return self.generateDetailPanels(); }
    );
    document.body.appendChild(overlay);
  };

  GroupedOptionsManager.prototype.openModal = function(optionType) {
    optionType = optionType || 'required';
    this.currentModalType = optionType;
    
    // バックアップを作成
    this.selectedOptionsBackup = new Map();
    var self = this;
    this.selectedOptions.forEach(function(value, key) {
      self.selectedOptionsBackup.set(key, JSON.parse(JSON.stringify(value)));
    });
    
    var overlay = document.getElementById('grouped-options-overlay');
    if (overlay) {
      var optionsToShow = optionType === 'required' ? this.requiredOptions : this.optionalOptions;
      var filteredOptions = this.updateModalContent(optionsToShow);
      this.restoreSelections();
      overlay.classList.add('active');
      
      if (filteredOptions && filteredOptions.length > 0) {
        this.selectCategory(filteredOptions[0].id);
      }
    }
  };

  GroupedOptionsManager.prototype.closeModal = function() {
    var overlay = document.getElementById('grouped-options-overlay');
    if (overlay) {
      overlay.classList.remove('active');
    }
    this.selectedOptionsBackup = null;
  };

  GroupedOptionsManager.prototype.cancelSelection = function() {
    
    if (this.selectedOptionsBackup !== null) {
      this.selectedOptions = new Map();
      var self = this;
      this.selectedOptionsBackup.forEach(function(value, key) {
        self.selectedOptions.set(key, value);
      });
    }
    
    this.closeModal();
    this.createSidebarButtons();
  };

  GroupedOptionsManager.prototype.updateModalContent = function(optionsToShow) {
    var printColor = Visibility.getCurrentPrintColor();
    var rule = Visibility.getColorVisibilityRule(printColor, this.colorVisibilityRules);
    
    var filteredOptions = optionsToShow.filter(function(option) {
      return Visibility.isOptionVisible(option, rule);
    });
    
    var categoryList = document.getElementById('options-category-list');
    if (categoryList) {
      var self = this;
      categoryList.innerHTML = filteredOptions.map(function(option) {
        return UIGenerator.generateCategoryItem(option, option.required, self.selectedOptions);
      }).join('');
    }
    
    var detailPanel = document.getElementById('options-detail-panel');
    if (detailPanel) {
      detailPanel.innerHTML = filteredOptions.map(function(option, index) {
        return UIGenerator.generateDetailPanel(option, index === 0, rule);
      }).join('');
    }
    
    return filteredOptions;
  };

  GroupedOptionsManager.prototype.selectCategory = function(optionId) {
    this.currentCategory = optionId;
    
    document.querySelectorAll('.options-category-item').forEach(function(item) {
      item.classList.remove('active');
    });
    
    var selectedItem = document.querySelector('[data-option-id="' + optionId + '"]');
    if (selectedItem) selectedItem.classList.add('active');
    
    document.querySelectorAll('.option-detail-section').forEach(function(section) {
      section.classList.remove('active');
    });
    
    var selectedSection = document.querySelector('.option-detail-section[data-option-id="' + optionId + '"]');
    if (selectedSection) selectedSection.classList.add('active');
  };

  GroupedOptionsManager.prototype.restoreSelections = function() {
    var self = this;
    
    this.selectedOptions.forEach(function(selection, optionId) {
      // ラジオボタンまたはチェックボックスを探す
      var input = document.querySelector('input[name="grouped-option-' + optionId + '"][value="' + selection.value + '"]');
      if (input) {
        input.checked = true;
        self.handleChoiceChange(input);
        
        // カスタムサイズの復元
        if (selection.customSize && (selection.width !== undefined || selection.height !== undefined)) {
          setTimeout(function() {
            var widthInput = document.getElementById('custom-width-' + optionId);
            var heightInput = document.getElementById('custom-height-' + optionId);
            
            if (widthInput && selection.width !== null && selection.width !== undefined) {
              widthInput.value = selection.width;
            }
            if (heightInput && selection.height !== null && selection.height !== undefined) {
              heightInput.value = selection.height;
            }
          }, 100);
        }
        
        // 追加入力フィールドの復元
        if (selection.additionalFieldValues) {
          setTimeout(function() {
            // 分納フォームの復元
            if (selection.additionalFieldValues.split_delivery_data) {
              self.restoreSplitDeliveryData(optionId, selection.additionalFieldValues.split_delivery_data);
            } else {
              // 通常の追加入力フィールドの復元
              Object.keys(selection.additionalFieldValues).forEach(function(fieldId) {
                var fieldInput = document.getElementById('additional-field-' + optionId + '-' + fieldId);
                if (fieldInput) {
                  fieldInput.value = selection.additionalFieldValues[fieldId];
                }
              });
            }
          }, 100);
        }
      }
    });
  };
  
  /**
   * 分納フォームデータの復元
   * @param {string} optionId - オプションID
   * @param {string} jsonData - JSON文字列
   */
  GroupedOptionsManager.prototype.restoreSplitDeliveryData = function(optionId, jsonData) {
    var self = this;
    try {
      var data = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
      if (!data || !data.destinations || !Array.isArray(data.destinations)) {
        return;
      }
      
      
      var list = document.getElementById('split-delivery-list-' + optionId);
      var container = document.getElementById('additional-fields-' + optionId);
      if (!list || !container) return;
      
      // 現在のアイテム数を取得
      var currentItems = list.querySelectorAll('.split-delivery-item');
      var currentCount = currentItems.length;
      
      // 必要に応じてアイテムを追加
      while (currentCount < data.destinations.length) {
        this.addSplitDeliveryItem(optionId);
        currentCount++;
      }
      
      // 少し遅延してからデータを設定（DOMの生成を待つ）
      setTimeout(function() {
        data.destinations.forEach(function(dest, idx) {
          var index = idx + 1;
          
          // 発送先情報
          self.setInputValue('split_dest_company_' + index, dest.company_name);
          self.setInputValue('split_dest_name_' + index, dest.recipient_name);
          self.setInputValue('split_dest_kana_' + index, dest.recipient_kana);
          self.setInputValue('split_dest_zip_' + index, dest.zip);
          self.setSelectValue('split_dest_prefecture_' + index, dest.prefecture);
          self.setInputValue('split_dest_address_' + index, dest.city_address);
          self.setInputValue('split_dest_building_' + index, dest.building);
          self.setInputValue('split_dest_phone_' + index, dest.phone);
          self.setInputValue('split_dest_quantity_' + index, dest.quantity);
          
          // 荷主情報
          var senderSameCheckbox = document.querySelector('input[name="split_dest_sender_same_' + index + '"]');
          if (senderSameCheckbox) {
            senderSameCheckbox.checked = dest.use_orderer_as_sender !== false;
            // チェックボックスのchangeイベントを発火してUIを更新
            var event = new Event('change', { bubbles: true });
            senderSameCheckbox.dispatchEvent(event);
          }
          
          if (!dest.use_orderer_as_sender) {
            self.setInputValue('split_dest_sender_name_' + index, dest.sender_name);
            self.setInputValue('split_dest_sender_zip_' + index, dest.sender_zip);
            self.setSelectValue('split_dest_sender_prefecture_' + index, dest.sender_prefecture);
            self.setInputValue('split_dest_sender_address_' + index, dest.sender_address);
            self.setInputValue('split_dest_sender_building_' + index, dest.sender_building);
            self.setInputValue('split_dest_sender_phone_' + index, dest.sender_phone);
          }
          
          // 備考
          self.setTextareaValue('split_dest_note_' + index, dest.note);
        });
        
        // サマリを更新
        var orderQuantity = window.priceCalculator ? window.priceCalculator.currentSelection.quantity : 0;
        self.updateSplitDeliverySummary(optionId, orderQuantity);
      }, 150);
      
    } catch (e) {
      console.error('[GroupedOptions] 分納データの復元エラー:', e);
    }
  };
  
  // 入力補助メソッド
  GroupedOptionsManager.prototype.setInputValue = function(name, value) {
    var input = document.querySelector('input[name="' + name + '"]');
    if (input && value !== undefined && value !== null) {
      input.value = value;
    }
  };
  
  GroupedOptionsManager.prototype.setSelectValue = function(name, value) {
    var select = document.querySelector('select[name="' + name + '"]');
    if (select && value !== undefined && value !== null) {
      select.value = value;
    }
  };
  
  GroupedOptionsManager.prototype.setTextareaValue = function(name, value) {
    var textarea = document.querySelector('textarea[name="' + name + '"]');
    if (textarea && value !== undefined && value !== null) {
      textarea.value = value;
    }
  };

  // =========================================
  // 選択処理
  // =========================================

  GroupedOptionsManager.prototype.handleChoiceChange = function(input) {
    var optionId = input.name.replace('grouped-option-', '');
    var isCustomSize = input.dataset.isCustomSize === 'true';
    var isCheckbox = input.type === 'checkbox';
    var self = this;
    
    // カスタムサイズ入力の表示/非表示
    var customSizeInput = document.getElementById('custom-size-input-' + optionId);
    if (customSizeInput) {
      if (isCustomSize) {
        customSizeInput.classList.add('active');
      } else {
        customSizeInput.classList.remove('active');
      }
    }
    
    // 追加入力フィールドの有効/無効切り替え
    var additionalFieldsContainer = document.getElementById('additional-fields-' + optionId);
    if (additionalFieldsContainer) {
      // checkboxの場合
      if (isCheckbox) {
        if (input.checked) {
          // チェックされた場合、入力フィールドを有効化
          additionalFieldsContainer.classList.remove('disabled');
          additionalFieldsContainer.querySelectorAll('input, textarea, select').forEach(function(field) {
            field.disabled = false;
          });
        } else {
          // チェックを外した場合、入力フィールドを無効化＆値クリア
          additionalFieldsContainer.classList.add('disabled');
          additionalFieldsContainer.querySelectorAll('input, textarea, select').forEach(function(field) {
            field.disabled = true;
            if (field.tagName === 'SELECT') {
              field.selectedIndex = 0; // セレクトボックスを最初の選択肢にリセット
            } else {
              field.value = '';
            }
            field.classList.remove('input-error');
          });
          var errorEl = additionalFieldsContainer.querySelector('.additional-fields-error');
          if (errorEl) errorEl.style.display = 'none';
        }
      }
      // radioボタンの場合
      else if (input.type === 'radio') {
        // 否定的な値（「希望しない」「不要」など）かどうかを判定
        var disableValue = input.value === '希望しない' || input.value === '利用しない' ||
                          input.value === 'いいえ' || input.value === 'no' || input.value === '不要';

        // オプションの選択肢数を確認（1つしかない場合は選択で有効化）
        var option = self.allOptions.find(function(opt) { return opt.id === optionId; });
        var hasSingleChoice = option && option.choices && option.choices.length === 1;

        // 肯定的な値かどうかを判定
        var enableValue = input.value === '希望する' || input.value === '利用する' ||
                          input.value === 'はい' || input.value === 'yes' || input.value === '必要';

        // 有効化条件：肯定的な値、または選択肢が1つしかない場合（否定的な値でない限り）
        if (enableValue || (hasSingleChoice && !disableValue)) {
          // 追加入力フィールドを有効化
          additionalFieldsContainer.classList.remove('disabled');
          additionalFieldsContainer.querySelectorAll('input, textarea, select').forEach(function(field) {
            field.disabled = false;
          });
        } else if (disableValue) {
          // 追加入力フィールドを無効化＆値クリア
          additionalFieldsContainer.classList.add('disabled');
          additionalFieldsContainer.querySelectorAll('input, textarea, select').forEach(function(field) {
            field.disabled = true;
            if (field.tagName === 'SELECT') {
              field.selectedIndex = 0;
            } else {
              field.value = '';
            }
            field.classList.remove('input-error');
          });
          var errorEl = additionalFieldsContainer.querySelector('.additional-fields-error');
          if (errorEl) errorEl.style.display = 'none';
        }
      }
    }
    
    // 元のオプションデータを取得
    var option = this.allOptions.find(function(opt) { return opt.id === optionId; });
    var choiceData = null;
    
    if (option && option.choices) {
      var rawChoice = option.choices.find(function(c) {
        var normalized = Utils.normalizeChoice(c);
        return normalized.value === input.value || normalized.label === input.value;
      });
      if (rawChoice) {
        choiceData = Utils.normalizeChoice(rawChoice);
      }
    }
    
    // checkboxの場合、チェックが外れたら選択を削除
    if (isCheckbox && !input.checked) {
      this.selectedOptions.delete(optionId);
      this.updateCategoryCheckmarks();
      return;
    }
    
    // 選択状態を保存
    var selectionData = {
      value: input.value,
      customSize: isCustomSize,
      width: isCustomSize ? (document.getElementById('custom-width-' + optionId) || {}).value : null,
      height: isCustomSize ? (document.getElementById('custom-height-' + optionId) || {}).value : null,
      choiceData: choiceData,
      isCheckbox: isCheckbox
    };
    
    // 追加入力フィールドの値を取得
    if (option && option.additionalFields && option.additionalFields.length > 0) {
      selectionData.additionalFieldValues = this.getAdditionalFieldValues(optionId, option.additionalFields);
    }
    
    this.selectedOptions.set(optionId, selectionData);
    
    // 排他オプションの処理（分納と納品代行は同時選択不可）
    this.handleMutuallyExclusiveOptions(option, optionId);
    
    this.updateCategoryCheckmarks();
  };
  
  /**
   * 排他オプションの処理
   * 分納と納品代行は同時に選択できない
   */
  GroupedOptionsManager.prototype.handleMutuallyExclusiveOptions = function(selectedOption, selectedOptionId) {
    if (!selectedOption || !selectedOption.label) return;
    
    var self = this;
    var selectedLabel = selectedOption.label.toLowerCase();
    
    // 分納と納品代行は排他的
    var isSplitDelivery = selectedLabel.includes('分納');
    var isDeliveryAgent = selectedLabel.includes('納品代行');
    
    if (!isSplitDelivery && !isDeliveryAgent) return;
    
    // 排他対象を探して解除
    var excludeKeyword = isSplitDelivery ? '納品代行' : '分納';
    
    this.allOptions.forEach(function(option) {
      if (option.id === selectedOptionId) return;
      if (!option.label) return;
      
      if (option.label.includes(excludeKeyword)) {
        // 選択されている場合は解除
        if (self.selectedOptions.has(option.id)) {
          self.selectedOptions.delete(option.id);
          
          // UIも更新
          var excludeInput = document.querySelector('input[name="grouped-option-' + option.id + '"]:checked');
          if (excludeInput) {
            excludeInput.checked = false;
          }
          
          // 警告メッセージを表示
          var validationArea = document.getElementById('grouped-options-validation');
          if (validationArea) {
            validationArea.textContent = '※「分納」と「納品代行」は同時に選択できません';
            validationArea.classList.add('active');
            setTimeout(function() {
              validationArea.classList.remove('active');
            }, 3000);
          }
        }
      }
    });
  };

  /**
   * 追加入力フィールドの値を取得
   * @param {string} optionId - オプションID
   * @param {Array} additionalFields - 追加フィールド定義
   * @returns {Object} フィールド値のオブジェクト
   */
  GroupedOptionsManager.prototype.getAdditionalFieldValues = function(optionId, additionalFields) {
    var values = {};
    
    // 分納フォームの場合は別処理
    var splitDeliveryContainer = document.getElementById('additional-fields-' + optionId);
    if (splitDeliveryContainer && splitDeliveryContainer.classList.contains('split-delivery-container')) {
      var splitData = this.getSplitDeliveryData(optionId);
      if (splitData) {
        values.split_delivery_data = JSON.stringify(splitData);
      }
      return values;
    }
    
    additionalFields.forEach(function(field) {
      var inputElement = document.getElementById('additional-field-' + optionId + '-' + field.id);
      if (inputElement) {
        values[field.id] = inputElement.value;
      }
    });
    return values;
  };

  // =========================================
  // 分納指定フォーム管理
  // =========================================

  /**
   * 分納：配送先を追加
   * @param {string} optionId - オプションID
   */
  GroupedOptionsManager.prototype.addSplitDeliveryItem = function(optionId) {
    var list = document.getElementById('split-delivery-list-' + optionId);
    var container = document.getElementById('additional-fields-' + optionId);
    if (!list || !container) return;
    
    var currentItems = list.querySelectorAll('.split-delivery-item');
    var currentCount = currentItems.length;
    
    // 最大10件チェック
    if (currentCount >= 10) {
      alert('配送先は最大10件までです');
      return;
    }
    
    var newIndex = currentCount + 1;
    var isDisabled = container.classList.contains('disabled');
    var disabledAttr = isDisabled ? 'disabled' : '';
    
    // 新しいアイテムを生成
    var UIGenerator = window.GroupedOptionsUIGenerator;
    var newItemHtml = UIGenerator.generateSplitDeliveryItem(optionId, newIndex, disabledAttr);
    
    // リストに追加
    list.insertAdjacentHTML('beforeend', newItemHtml);
    
    // 残り数量を自動入力
    this.autoFillRemainingQuantity(optionId, newIndex);
    
    // サマリを更新
    this.updateSplitDeliverySummary(optionId);
    
    // 新しく追加されたアイテムにスクロール
    var newItem = document.getElementById('split-delivery-item-' + optionId + '-' + newIndex);
    if (newItem) {
      newItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    
  };

  /**
   * 分納：残り数量を自動入力
   * @param {string} optionId - オプションID
   * @param {number} targetIndex - 入力対象のインデックス
   */
  GroupedOptionsManager.prototype.autoFillRemainingQuantity = function(optionId, targetIndex) {
    var list = document.getElementById('split-delivery-list-' + optionId);
    if (!list) return;
    
    var orderQuantity = this.getOrderQuantity();
    if (orderQuantity <= 0) return;
    
    var items = list.querySelectorAll('.split-delivery-item');
    var currentTotal = 0;
    
    // 他の配送先の数量合計を計算
    items.forEach(function(item) {
      var index = parseInt(item.dataset.index);
      if (index !== targetIndex) {
        var qtyInput = item.querySelector('.split-delivery-quantity');
        if (qtyInput && qtyInput.value) {
          currentTotal += parseInt(qtyInput.value) || 0;
        }
      }
    });
    
    // 残り数量を計算
    var remaining = orderQuantity - currentTotal;
    
    // 残り数量が正の場合、新しい配送先に自動入力
    if (remaining > 0) {
      var targetItem = document.getElementById('split-delivery-item-' + optionId + '-' + targetIndex);
      if (targetItem) {
        var qtyInput = targetItem.querySelector('.split-delivery-quantity');
        if (qtyInput && !qtyInput.value) {
          qtyInput.value = remaining;
        }
      }
    }
  };

  /**
   * 分納：配送先を削除
   * @param {string} optionId - オプションID
   * @param {number} index - 削除するインデックス
   */
  GroupedOptionsManager.prototype.removeSplitDeliveryItem = function(optionId, index) {
    var list = document.getElementById('split-delivery-list-' + optionId);
    if (!list) return;
    
    var item = document.getElementById('split-delivery-item-' + optionId + '-' + index);
    if (item) {
      item.remove();
      
      // インデックスを振り直し
      this.reindexSplitDeliveryItems(optionId);
      
      // サマリを更新
      this.updateSplitDeliverySummary(optionId);
      
    }
  };

  /**
   * 分納：アイテムのインデックスを振り直し
   * @param {string} optionId - オプションID
   */
  GroupedOptionsManager.prototype.reindexSplitDeliveryItems = function(optionId) {
    var list = document.getElementById('split-delivery-list-' + optionId);
    if (!list) return;
    
    var items = list.querySelectorAll('.split-delivery-item');
    items.forEach(function(item, idx) {
      var newIndex = idx + 1;
      var oldIndex = parseInt(item.dataset.index);
      
      // IDと属性を更新
      item.id = 'split-delivery-item-' + optionId + '-' + newIndex;
      item.dataset.index = newIndex;
      
      // タイトル更新
      var title = item.querySelector('.split-delivery-item-title');
      if (title) {
        title.textContent = '配送先 ' + newIndex;
      }
      
      // 削除ボタンの表示/非表示と属性更新
      var removeBtn = item.querySelector('.split-delivery-remove-btn');
      if (newIndex === 1) {
        if (removeBtn) removeBtn.remove();
      } else {
        if (removeBtn) {
          removeBtn.dataset.index = newIndex;
        } else {
          // 削除ボタンがない場合は追加
          var header = item.querySelector('.split-delivery-item-header');
          if (header) {
            var btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'split-delivery-remove-btn';
            btn.dataset.index = newIndex;
            btn.textContent = '削除';
            header.appendChild(btn);
          }
        }
      }
      
      // input要素のname属性を更新
      var inputs = item.querySelectorAll('input');
      inputs.forEach(function(input) {
        var name = input.name;
        if (name) {
          input.name = name.replace(/_\d+$/, '_' + newIndex);
        }
      });
    });
  };

  /**
   * 分納：注文数量を取得
   * @returns {number} 注文数量
   */
  GroupedOptionsManager.prototype.getOrderQuantity = function() {
    // DynamicPriceCalculatorから現在の数量を取得
    if (window.priceCalculator && window.priceCalculator.currentSelection) {
      return parseInt(window.priceCalculator.currentSelection.quantity) || 0;
    }
    // フォールバック: セレクターから取得
    var qtySelect = document.getElementById('quantity-select');
    if (qtySelect && qtySelect.value) {
      return parseInt(qtySelect.value) || 0;
    }
    return 0;
  };

  /**
   * 分納：サマリを更新
   * @param {string} optionId - オプションID
   */
  GroupedOptionsManager.prototype.updateSplitDeliverySummary = function(optionId) {
    var list = document.getElementById('split-delivery-list-' + optionId);
    var countEl = document.getElementById('split-delivery-count-' + optionId);
    var totalEl = document.getElementById('split-delivery-total-' + optionId);
    var feeEl = document.getElementById('split-delivery-fee-' + optionId);
    var addBtn = document.getElementById('split-delivery-add-' + optionId);
    var errorEl = document.getElementById('split-delivery-error-' + optionId);
    
    if (!list) return;
    
    var items = list.querySelectorAll('.split-delivery-item');
    var count = items.length;
    var total = 0;
    var orderQuantity = this.getOrderQuantity();
    
    // 数量を合計
    items.forEach(function(item) {
      var qtyInput = item.querySelector('.split-delivery-quantity');
      if (qtyInput && qtyInput.value) {
        total += parseInt(qtyInput.value) || 0;
      }
    });
    
    // 追加料金計算（2件目以降、1件ごとに+2,200円）
    var fee = Math.max(0, count - 1) * 2200;
    
    // 残り数量計算
    var remaining = orderQuantity - total;
    
    // 表示更新
    if (countEl) countEl.textContent = count + '件';
    if (totalEl) {
      var totalText = total.toLocaleString() + '部';
      if (orderQuantity > 0) {
        totalText += ' / ' + orderQuantity.toLocaleString() + '部';
        if (remaining !== 0) {
          if (remaining > 0) {
            totalText += ' <span class="remaining-quantity warning">（残り' + remaining.toLocaleString() + '部）</span>';
          } else {
            totalText += ' <span class="remaining-quantity error">（' + Math.abs(remaining).toLocaleString() + '部超過）</span>';
          }
        } else {
          totalText += ' <span class="remaining-quantity success">✓</span>';
        }
      }
      totalEl.innerHTML = totalText;
    }
    if (feeEl) {
      feeEl.textContent = '+' + fee.toLocaleString() + '円';
      if (fee > 0) {
        feeEl.classList.add('has-fee');
      } else {
        feeEl.classList.remove('has-fee');
      }
    }
    
    // バリデーションエラー表示
    if (errorEl) {
      if (orderQuantity > 0 && remaining !== 0) {
        if (remaining > 0) {
          errorEl.textContent = '⚠️ 数量の合計が注文数量（' + orderQuantity.toLocaleString() + '部）に達していません。残り' + remaining.toLocaleString() + '部を入力してください。';
          errorEl.className = 'split-delivery-validation-error warning';
        } else {
          errorEl.textContent = '❌ 数量の合計が注文数量（' + orderQuantity.toLocaleString() + '部）を' + Math.abs(remaining).toLocaleString() + '部超過しています。';
          errorEl.className = 'split-delivery-validation-error error';
        }
        errorEl.style.display = 'block';
      } else {
        errorEl.style.display = 'none';
      }
    }
    
    // 残り追加可能件数
    var remainingItems = 10 - count;
    if (addBtn) {
      var remainingSpan = addBtn.querySelector('.remaining-count');
      if (remainingSpan) {
        remainingSpan.textContent = '(残り' + remainingItems + '件追加可能)';
      }
      if (remainingItems <= 0) {
        addBtn.disabled = true;
        addBtn.style.opacity = '0.5';
      } else {
        addBtn.disabled = false;
        addBtn.style.opacity = '1';
      }
    }
    
    // 各数量入力欄の最大値を設定
    this.updateQuantityMaxValues(optionId, orderQuantity);
    
    // 隠しフィールドにJSONデータを保存
    var dataInput = document.getElementById('split-delivery-data-' + optionId);
    if (dataInput) {
      var data = this.getSplitDeliveryData(optionId);
      dataInput.value = JSON.stringify(data);
    }
    
    // 選択状態を更新
    var currentSelection = this.selectedOptions.get(optionId);
    if (currentSelection) {
      currentSelection.additionalFieldValues = {
        split_delivery_data: JSON.stringify(this.getSplitDeliveryData(optionId))
      };
      this.selectedOptions.set(optionId, currentSelection);
    }
  };

  /**
   * 分納：各数量入力欄の最大値を設定し、残り数量を表示
   * @param {string} optionId - オプションID
   * @param {number} orderQuantity - 注文数量
   */
  GroupedOptionsManager.prototype.updateQuantityMaxValues = function(optionId, orderQuantity) {
    var list = document.getElementById('split-delivery-list-' + optionId);
    if (!list) return;
    
    var items = list.querySelectorAll('.split-delivery-item');
    var currentTotal = 0;
    
    // まず現在の合計を計算
    items.forEach(function(item) {
      var qtyInput = item.querySelector('.split-delivery-quantity');
      if (qtyInput && qtyInput.value) {
        currentTotal += parseInt(qtyInput.value) || 0;
      }
    });
    
    // 全体の残り数量
    var totalRemaining = orderQuantity - currentTotal;
    
    // 各入力欄の最大値と残り数量を設定
    items.forEach(function(item) {
      var index = parseInt(item.dataset.index);
      var qtyInput = item.querySelector('.split-delivery-quantity');
      var orderInfoEl = document.getElementById('split-order-info-' + optionId + '-' + index);
      var remainingEl = document.getElementById('split-remaining-' + optionId + '-' + index);
      
      // 注文数量情報を表示
      if (orderInfoEl && orderQuantity > 0) {
        orderInfoEl.innerHTML = '（注文数量: <strong>' + orderQuantity.toLocaleString() + '</strong>部）';
      }
      
      if (qtyInput) {
        var currentValue = parseInt(qtyInput.value) || 0;
        // この入力欄の最大値 = 注文数量 - 他の入力欄の合計
        var otherTotal = currentTotal - currentValue;
        var maxValue = orderQuantity > 0 ? orderQuantity - otherTotal : 0;
        
        if (orderQuantity > 0) {
          qtyInput.max = Math.max(0, maxValue);
        }
        
        // 残り数量情報を表示
        if (remainingEl && orderQuantity > 0) {
          var itemRemaining = maxValue - currentValue;
          if (currentValue > 0 && itemRemaining === 0 && totalRemaining === 0) {
            remainingEl.innerHTML = '<span class="remaining-badge success">✓ 配分完了</span>';
          } else if (currentValue > maxValue) {
            remainingEl.innerHTML = '<span class="remaining-badge error">' + Math.abs(itemRemaining).toLocaleString() + '部 超過</span>';
          } else if (totalRemaining > 0) {
            remainingEl.innerHTML = '<span class="remaining-badge info">残り <strong>' + totalRemaining.toLocaleString() + '</strong>部</span>';
          } else if (totalRemaining < 0) {
            remainingEl.innerHTML = '<span class="remaining-badge error">合計が ' + Math.abs(totalRemaining).toLocaleString() + '部 超過</span>';
          } else {
            remainingEl.innerHTML = '<span class="remaining-badge success">✓ 配分完了</span>';
          }
        }
        
        // 現在値が最大値を超えている場合の警告スタイル
        if (orderQuantity > 0 && currentValue > maxValue && maxValue >= 0) {
          qtyInput.classList.add('input-over-limit');
        } else {
          qtyInput.classList.remove('input-over-limit');
        }
      }
    });
  };

  /**
   * 分納：データを取得
   * @param {string} optionId - オプションID
   * @returns {Object} 分納データ
   */
  GroupedOptionsManager.prototype.getSplitDeliveryData = function(optionId) {
    var list = document.getElementById('split-delivery-list-' + optionId);
    if (!list) return null;
    
    var items = list.querySelectorAll('.split-delivery-item');
    var destinations = [];
    var totalQuantity = 0;
    
    items.forEach(function(item, idx) {
      var index = idx + 1;
      
      // 発送先情報
      var companyInput = item.querySelector('input[name="split_dest_company_' + index + '"]');
      var nameInput = item.querySelector('input[name="split_dest_name_' + index + '"]');
      var kanaInput = item.querySelector('input[name="split_dest_kana_' + index + '"]');
      var zipInput = item.querySelector('input[name="split_dest_zip_' + index + '"]');
      var prefectureInput = item.querySelector('select[name="split_dest_prefecture_' + index + '"]');
      var addressInput = item.querySelector('input[name="split_dest_address_' + index + '"]');
      var buildingInput = item.querySelector('input[name="split_dest_building_' + index + '"]');
      var phoneInput = item.querySelector('input[name="split_dest_phone_' + index + '"]');
      var quantityInput = item.querySelector('input[name="split_dest_quantity_' + index + '"]');
      
      // 荷主情報
      var senderSameCheckbox = item.querySelector('input[name="split_dest_sender_same_' + index + '"]');
      var senderNameInput = item.querySelector('input[name="split_dest_sender_name_' + index + '"]');
      var senderZipInput = item.querySelector('input[name="split_dest_sender_zip_' + index + '"]');
      var senderPrefectureInput = item.querySelector('select[name="split_dest_sender_prefecture_' + index + '"]');
      var senderAddressInput = item.querySelector('input[name="split_dest_sender_address_' + index + '"]');
      var senderBuildingInput = item.querySelector('input[name="split_dest_sender_building_' + index + '"]');
      var senderPhoneInput = item.querySelector('input[name="split_dest_sender_phone_' + index + '"]');
      
      // 備考
      var noteInput = item.querySelector('textarea[name="split_dest_note_' + index + '"]');
      
      var quantity = parseInt(quantityInput ? quantityInput.value : 0) || 0;
      totalQuantity += quantity;
      
      var useSenderSame = senderSameCheckbox ? senderSameCheckbox.checked : true;
      
      destinations.push({
        index: index,
        // 発送先情報
        company_name: companyInput ? companyInput.value.trim() : '',
        recipient_name: nameInput ? nameInput.value.trim() : '',
        recipient_kana: kanaInput ? kanaInput.value.trim() : '',
        zip: zipInput ? zipInput.value.trim() : '',
        prefecture: prefectureInput ? prefectureInput.value : '',
        city_address: addressInput ? addressInput.value.trim() : '',
        building: buildingInput ? buildingInput.value.trim() : '',
        phone: phoneInput ? phoneInput.value.trim() : '',
        quantity: quantity,
        // 荷主情報
        use_orderer_as_sender: useSenderSame,
        sender_name: !useSenderSame && senderNameInput ? senderNameInput.value.trim() : '',
        sender_zip: !useSenderSame && senderZipInput ? senderZipInput.value.trim() : '',
        sender_prefecture: !useSenderSame && senderPrefectureInput ? senderPrefectureInput.value : '',
        sender_address: !useSenderSame && senderAddressInput ? senderAddressInput.value.trim() : '',
        sender_building: !useSenderSame && senderBuildingInput ? senderBuildingInput.value.trim() : '',
        sender_phone: !useSenderSame && senderPhoneInput ? senderPhoneInput.value.trim() : '',
        // 備考
        note: noteInput ? noteInput.value.trim() : ''
      });
    });
    
    var fee = Math.max(0, destinations.length - 1) * 2200;
    
    return {
      enabled: true,
      destination_count: destinations.length,
      total_quantity: totalQuantity,
      additional_fee: fee,
      destinations: destinations
    };
  };

  /**
   * 分納：荷主情報の表示/非表示を切り替え
   * @param {HTMLElement} checkbox - チェックボックス要素
   */
  GroupedOptionsManager.prototype.toggleSenderFields = function(checkbox) {
    var item = checkbox.closest('.split-delivery-item');
    if (!item) return;
    
    var senderFields = item.querySelector('.split-delivery-sender-fields');
    if (!senderFields) return;
    
    if (checkbox.checked) {
      senderFields.style.display = 'none';
      // 荷主入力フィールドの必須属性を解除
      senderFields.querySelectorAll('.split-delivery-sender-input').forEach(function(input) {
        input.removeAttribute('required');
      });
    } else {
      senderFields.style.display = 'block';
      // 荷主入力フィールドに必須属性を設定（一部のみ）
      var senderNameInput = senderFields.querySelector('input[name^="split_dest_sender_name_"]');
      var senderPrefectureInput = senderFields.querySelector('select[name^="split_dest_sender_prefecture_"]');
      var senderAddressInput = senderFields.querySelector('input[name^="split_dest_sender_address_"]');
      var senderPhoneInput = senderFields.querySelector('input[name^="split_dest_sender_phone_"]');
      
      if (senderNameInput) senderNameInput.setAttribute('required', 'required');
      if (senderPrefectureInput) senderPrefectureInput.setAttribute('required', 'required');
      if (senderAddressInput) senderAddressInput.setAttribute('required', 'required');
      if (senderPhoneInput) senderPhoneInput.setAttribute('required', 'required');
    }
  };

  /**
   * 分納：バリデーション
   * @param {string} optionId - オプションID
   * @param {number} orderQuantity - 注文数量
   * @returns {Object} バリデーション結果
   */
  GroupedOptionsManager.prototype.validateSplitDelivery = function(optionId, orderQuantity) {
    var data = this.getSplitDeliveryData(optionId);
    var errors = [];
    
    if (!data || data.destinations.length === 0) {
      errors.push('配送先を1件以上入力してください');
      return { valid: false, errors: errors };
    }
    
    // 各配送先のバリデーション
    data.destinations.forEach(function(dest, idx) {
      var prefix = '配送先' + (idx + 1) + ': ';
      
      // 発送先情報の必須項目
      if (!dest.recipient_name) errors.push(prefix + 'お名前を入力してください');
      if (!dest.recipient_kana) errors.push(prefix + 'フリガナを入力してください');
      if (!dest.zip) errors.push(prefix + '郵便番号を入力してください');
      if (!dest.prefecture) errors.push(prefix + '都道府県を選択してください');
      if (!dest.city_address) errors.push(prefix + '市区町村番地を入力してください');
      if (!dest.phone) errors.push(prefix + '電話番号を入力してください');
      if (!dest.quantity || dest.quantity <= 0) errors.push(prefix + '数量を入力してください');
      
      // 荷主情報のバリデーション（「注文者情報と同じ」でない場合）
      if (!dest.use_orderer_as_sender) {
        if (!dest.sender_name) errors.push(prefix + '荷主名を入力してください');
        if (!dest.sender_zip) errors.push(prefix + '荷主の郵便番号を入力してください');
        if (!dest.sender_prefecture) errors.push(prefix + '荷主の都道府県を選択してください');
        if (!dest.sender_address) errors.push(prefix + '荷主の住所を入力してください');
        if (!dest.sender_phone) errors.push(prefix + '荷主の電話番号を入力してください');

        // 荷主郵便番号形式チェック
        if (dest.sender_zip && !/^\d{3}-?\d{4}$/.test(dest.sender_zip)) {
          errors.push(prefix + '荷主の郵便番号の形式が正しくありません（例: 100-0001）');
        }
      }
      
      // 郵便番号形式チェック
      if (dest.zip && !/^\d{3}-?\d{4}$/.test(dest.zip)) {
        errors.push(prefix + '郵便番号の形式が正しくありません（例: 100-0001）');
      }
      
      // フリガナ形式チェック（カタカナ）
      if (dest.recipient_kana && !/^[ァ-ヶー\s]+$/.test(dest.recipient_kana)) {
        errors.push(prefix + 'フリガナはカタカナで入力してください');
      }
    });
    
    // 数量合計チェック
    if (orderQuantity && data.total_quantity !== orderQuantity) {
      errors.push('数量の合計（' + data.total_quantity + '部）が注文数量（' + orderQuantity + '部）と一致しません');
    }
    
    return {
      valid: errors.length === 0,
      errors: errors,
      data: data
    };
  };

  /**
   * 分納：エラーフィールドをハイライト
   * @param {string} optionId - オプションID
   * @param {Array} errors - エラーメッセージ配列
   */
  GroupedOptionsManager.prototype.highlightSplitDeliveryErrors = function(optionId, errors) {
    var list = document.getElementById('split-delivery-list-' + optionId);
    if (!list) return;

    // まず全てのエラーハイライトをクリア
    list.querySelectorAll('.split-delivery-input, .split-delivery-select').forEach(function(input) {
      input.classList.remove('input-error');
    });

    // エラーメッセージからフィールドを特定してハイライト
    var fieldMapping = {
      'お名前': 'split_dest_name_',
      'フリガナ': 'split_dest_kana_',
      '郵便番号を入力': 'split_dest_zip_',
      '都道府県を選択': 'split_dest_prefecture_',
      '市区町村番地': 'split_dest_city_',
      '電話番号を入力': 'split_dest_phone_',
      '数量を入力': 'split_dest_quantity_',
      '荷主名': 'split_dest_sender_name_',
      '荷主の郵便番号': 'split_dest_sender_zip_',
      '荷主の都道府県': 'split_dest_sender_prefecture_',
      '荷主の住所': 'split_dest_sender_address_',
      '荷主の電話番号': 'split_dest_sender_phone_'
    };

    var firstErrorField = null;

    errors.forEach(function(error) {
      // 配送先番号を抽出（例: "配送先1: お名前を入力してください"）
      var destMatch = error.match(/配送先(\d+)/);
      var destIndex = destMatch ? parseInt(destMatch[1]) : null;

      // どのフィールドのエラーかを特定
      Object.keys(fieldMapping).forEach(function(keyword) {
        if (error.includes(keyword)) {
          var fieldName = fieldMapping[keyword] + destIndex;
          var field = list.querySelector('[name="' + fieldName + '"]');
          if (field) {
            field.classList.add('input-error');
            if (!firstErrorField) {
              firstErrorField = field;
            }
          }
        }
      });
    });

    // 最初のエラーフィールドにフォーカス
    if (firstErrorField) {
      setTimeout(function() {
        firstErrorField.focus();
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
    }
  };

  GroupedOptionsManager.prototype.updateCategoryCheckmarks = function() {
    var self = this;
    this.allOptions.forEach(function(option) {
      var categoryItem = document.querySelector('.options-category-item[data-option-id="' + option.id + '"]');
      if (categoryItem) {
        if (self.selectedOptions.has(option.id)) {
          categoryItem.classList.add('selected');
        } else {
          categoryItem.classList.remove('selected');
        }
      }
    });
  };

  GroupedOptionsManager.prototype.clearOptionSelection = function(optionId) {
    
    this.selectedOptions.delete(optionId);
    
    // ラジオボタン/チェックボックスのクリア
    var inputs = document.querySelectorAll('input[name="grouped-option-' + optionId + '"]');
    inputs.forEach(function(input) {
      input.checked = false;
    });
    
    // カスタムサイズ入力のクリア
    var widthInput = document.getElementById('custom-width-' + optionId);
    var heightInput = document.getElementById('custom-height-' + optionId);
    if (widthInput) widthInput.value = '';
    if (heightInput) heightInput.value = '';
    
    var customSizeInput = document.getElementById('custom-size-input-' + optionId);
    if (customSizeInput) {
      customSizeInput.classList.remove('active');
    }
    
    // 追加入力フィールドの値クリア＆無効化
    var additionalFieldsContainer = document.getElementById('additional-fields-' + optionId);
    if (additionalFieldsContainer) {
      additionalFieldsContainer.classList.add('disabled');
      additionalFieldsContainer.querySelectorAll('input, textarea, select').forEach(function(field) {
        field.disabled = true;
        if (field.tagName === 'SELECT') {
          field.selectedIndex = 0;
        } else {
          field.value = '';
        }
        field.classList.remove('input-error');
      });
      var errorEl = additionalFieldsContainer.querySelector('.additional-fields-error');
      if (errorEl) errorEl.style.display = 'none';
    }
    
    this.updateCategoryCheckmarks();
    
    // ProductOptionsManagerからも削除して価格を再計算
    if (window.productOptionsManager) {
      var option = this.allOptions.find(function(opt) { return opt.id === optionId; });
      if (option) {
        if (option.required) {
          delete window.productOptionsManager.selectedOptions.required[optionId];
        } else {
          delete window.productOptionsManager.selectedOptions.optional[optionId];
        }
        
        // 価格を再計算
        if (typeof window.productOptionsManager.recalculatePrice === 'function') {
          window.productOptionsManager.recalculatePrice();
        }
        // カートプロパティを更新
        if (typeof window.productOptionsManager.updateCartProperties === 'function') {
          window.productOptionsManager.updateCartProperties();
        }
      }
    }
    
    // サイドバー更新用イベントを発火
    document.dispatchEvent(new CustomEvent('optionsUpdated', {
      detail: { selectedOptions: this.selectedOptions }
    }));
  };

  GroupedOptionsManager.prototype.confirmSelection = function() {
    var self = this;

    // 現在のモーダルタイプに属するオプションのみバリデーション対象とする
    var currentOptions = this.currentModalType === 'required'
      ? this.requiredOptions
      : this.optionalOptions;
    var currentOptionIds = new Set(currentOptions.map(function(opt) { return opt.id; }));

    // カスタムサイズのバリデーション
    var customSizeError = false;
    var customSizeOptionId = null;
    this.selectedOptions.forEach(function(selection, optionId) {
      // 現在のモーダルタイプに属しないオプションはスキップ
      if (!currentOptionIds.has(optionId)) return;

      if (selection.customSize) {
        var widthInput = document.getElementById('custom-width-' + optionId);
        var heightInput = document.getElementById('custom-height-' + optionId);
        var width = widthInput ? (widthInput.value || '').trim() : '';
        var height = heightInput ? (heightInput.value || '').trim() : '';
        
        if (!width || !height || isNaN(parseFloat(width)) || isNaN(parseFloat(height))) {
          customSizeError = true;
          customSizeOptionId = optionId;
          
          if (widthInput && !width) widthInput.classList.add('input-error');
          if (heightInput && !height) heightInput.classList.add('input-error');
          
          var errorEl = document.getElementById('custom-size-error-' + optionId);
          if (errorEl) {
            errorEl.textContent = '幅と高さを入力してください';
            errorEl.style.display = 'block';
          }
        } else {
          selection.width = width;
          selection.height = height;
          
          if (widthInput) widthInput.classList.remove('input-error');
          if (heightInput) heightInput.classList.remove('input-error');
          var errorEl = document.getElementById('custom-size-error-' + optionId);
          if (errorEl) errorEl.style.display = 'none';
        }
      }
    });
    
    if (customSizeError) {
      // フッターのバリデーションエリアにエラーを表示
      var validationArea = document.getElementById('grouped-options-validation');
      if (validationArea) {
        validationArea.textContent = '⚠️ 変型サイズの幅と高さを入力してください';
        validationArea.classList.remove('active');
        validationArea.classList.add('error');
        // アニメーションを再トリガー
        validationArea.style.animation = 'none';
        void validationArea.offsetWidth; // reflow
        validationArea.style.animation = '';
      }
      
      // カスタムサイズのオプションパネルに切り替えて入力フィールドを表示
      if (customSizeOptionId) {
        self.selectCategory(customSizeOptionId);
        // 入力フィールドにフォーカス
        var widthInput = document.getElementById('custom-width-' + customSizeOptionId);
        if (widthInput) {
          setTimeout(function() {
            widthInput.focus();
            widthInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
        }
      }
      return;
    }
    
    // エラーがない場合はバリデーションメッセージを非表示
    var validationArea = document.getElementById('grouped-options-validation');
    if (validationArea) {
      validationArea.classList.remove('error');
    }
    
    // 追加入力フィールドのバリデーション
    var additionalFieldsError = false;
    this.selectedOptions.forEach(function(selection, optionId) {
      // 現在のモーダルタイプに属しないオプションはスキップ
      if (!currentOptionIds.has(optionId)) return;

      var option = self.allOptions.find(function(opt) { return opt.id === optionId; });
      if (option && option.additionalFields && option.additionalFields.length > 0) {
        option.additionalFields.forEach(function(field) {
          if (field.required) {
            var inputEl = document.getElementById('additional-field-' + optionId + '-' + field.id);
            if (inputEl) {
              var value = (inputEl.value || '').trim();
              if (!value) {
                additionalFieldsError = true;
                inputEl.classList.add('input-error');
              } else {
                inputEl.classList.remove('input-error');
              }
            }
          }
        });
        
        // エラーがあれば追加フィールドコンテナにエラーメッセージを表示
        var errorEl = document.querySelector('#additional-fields-' + optionId + ' .additional-fields-error');
        if (errorEl) {
          if (additionalFieldsError) {
            errorEl.textContent = '必須項目を入力してください';
            errorEl.style.display = 'block';
          } else {
            errorEl.style.display = 'none';
          }
        }
        
        // バリデーション成功時、最新の値を取得
        if (!additionalFieldsError) {
          selection.additionalFieldValues = self.getAdditionalFieldValues(optionId, option.additionalFields);
        }
      }
    });
    
    if (additionalFieldsError) return;
    
    // 分納指定のバリデーション
    var splitDeliveryError = false;
    this.selectedOptions.forEach(function(selection, optionId) {
      // 現在のモーダルタイプに属しないオプションはスキップ
      if (!currentOptionIds.has(optionId)) return;

      var option = self.allOptions.find(function(opt) { return opt.id === optionId; });
      if (option && option.additionalFields) {
        var hasSplitDeliveryForm = option.additionalFields.some(function(f) {
          return f.type === 'split_delivery_form';
        });
        
        if (hasSplitDeliveryForm && selection.additionalFieldValues && selection.additionalFieldValues.split_delivery_data) {
          var orderQuantity = self.getOrderQuantity();
          var validation = self.validateSplitDelivery(optionId, orderQuantity);
          
          if (!validation.valid) {
            splitDeliveryError = true;
            var errorEl = document.getElementById('split-delivery-error-' + optionId);
            if (errorEl) {
              errorEl.innerHTML = validation.errors.map(function(e) { return '❌ ' + e; }).join('<br>');
              errorEl.className = 'split-delivery-validation-error error';
              errorEl.style.display = 'block';

              // エラーフィールドをハイライト
              self.highlightSplitDeliveryErrors(optionId, validation.errors);

              // エラー表示へスクロール＆シェイクアニメーション
              setTimeout(function() {
                errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                errorEl.classList.add('shake-animation');
                setTimeout(function() {
                  errorEl.classList.remove('shake-animation');
                }, 500);
              }, 100);
            }

            // 分納指定オプションにフォーカス
            self.selectCategory(optionId);
          }
        }
      }
    });
    
    if (splitDeliveryError) return;
    
    // 必須オプションのバリデーション
    if (this.currentModalType === 'required') {
      var printColor = Visibility.getCurrentPrintColor();
      var rule = Visibility.getColorVisibilityRule(printColor, this.colorVisibilityRules);
      var visibleRequiredOptions = this.requiredOptions.filter(function(opt) {
        return Visibility.isOptionVisible(opt, rule);
      });
      
      var requiredCount = visibleRequiredOptions.length;
      var selectedRequiredCount = visibleRequiredOptions.filter(function(opt) {
        return self.selectedOptions.has(opt.id);
      }).length;
      
      if (selectedRequiredCount < requiredCount) {
        var validation = document.getElementById('grouped-options-validation');
        if (validation) validation.classList.add('active');
        return;
      }
    }
    
    // ProductOptionsManagerに適用
    if (window.productOptionsManager) {
      this.selectedOptions.forEach(function(selection, optionId) {
        var option = self.allOptions.find(function(opt) { return opt.id === optionId; });
        if (option) {
          var choiceToSave = self.buildChoiceToSave(optionId, selection, option);

          if (option.required) {
            window.productOptionsManager.selectedOptions.required[optionId] = choiceToSave;
          } else {
            window.productOptionsManager.selectedOptions.optional[optionId] = choiceToSave;
          }
        }
      });
      
      if (typeof window.productOptionsManager.recalculatePrice === 'function') {
        window.productOptionsManager.recalculatePrice();
      }
      if (typeof window.productOptionsManager.updateCartProperties === 'function') {
        window.productOptionsManager.updateCartProperties();
      }
    }
    
    this.createSidebarButtons();
    this.closeModal();

    document.dispatchEvent(new CustomEvent('optionsUpdated', {
      detail: { selectedOptions: this.selectedOptions }
    }));
  };

  /**
   * 選択状態（selectedOptions の1件）から ProductOptionsManager に渡す選択肢を組み立てる
   * PC（confirmSelection）とモバイル（confirmMobileSelection）で共通
   * @param {string} optionId - オプションID
   * @param {Object} selection - selectedOptions の値
   * @param {Object} option - オプション
   * @returns {Object} 選択肢（price_modifier / lead_time_modifier を含む）
   */
  GroupedOptionsManager.prototype.buildChoiceToSave = function(optionId, selection, option) {
    var choiceToSave = {
      label: selection.value,
      value: selection.value
    };

    if (selection.choiceData) {
      choiceToSave = {
        label: selection.choiceData.label || selection.value,
        value: selection.choiceData.value || selection.value,
        price_modifier: selection.choiceData.price_modifier,
        lead_time_modifier: selection.choiceData.lead_time_modifier
      };
    }

    if (selection.customSize && selection.width && selection.height) {
      choiceToSave.customSize = {
        width: selection.width,
        height: selection.height
      };
    }

    // 追加入力フィールドの値を含める
    // 分納フォームの場合は最新データを取得
    var hasSplitDeliveryForm = option.additionalFields && option.additionalFields.some(function(f) {
      return f.type === 'split_delivery_form';
    });

    if (hasSplitDeliveryForm) {
      // 分納フォームの最新データを取得
      var latestSplitData = this.getSplitDeliveryData(optionId);
      if (latestSplitData) {
        choiceToSave.additionalFields = {
          split_delivery_data: JSON.stringify(latestSplitData)
        };
        // 分納追加料金をprice_modifierに設定
        if (latestSplitData.additional_fee && latestSplitData.additional_fee > 0) {
          choiceToSave.price_modifier = latestSplitData.additional_fee;
        }
      }
    } else if (selection.additionalFieldValues && Object.keys(selection.additionalFieldValues).length > 0) {
      choiceToSave.additionalFields = selection.additionalFieldValues;
    }

    return choiceToSave;
  };

  // =========================================
  // UI生成（委譲）
  // =========================================

  GroupedOptionsManager.prototype.generateCategoryList = function() {
    return UIGenerator.generateCategoryList(this.requiredOptions, this.optionalOptions, this.selectedOptions);
  };

  GroupedOptionsManager.prototype.generateDetailPanels = function() {
    var printColor = Visibility.getCurrentPrintColor();
    var rule = Visibility.getColorVisibilityRule(printColor, this.colorVisibilityRules);
    return UIGenerator.generateDetailPanels(this.requiredOptions, this.optionalOptions, rule);
  };

  GroupedOptionsManager.prototype.createSidebarButtons = function() {
    var container = document.getElementById('product-options-container');
    var mobileContainer = document.getElementById('mobile-product-options-container');
    
    if (!container && !mobileContainer) return;

    var printColor = Visibility.getCurrentPrintColor();
    var rule = Visibility.getColorVisibilityRule(printColor, this.colorVisibilityRules);
    var self = this;
    
    // 表示用パラメータを計算
    var visibleRequiredOptions = this.requiredOptions.filter(function(opt) {
      return Visibility.isOptionVisible(opt, rule);
    });
    var requiredCount = visibleRequiredOptions.length;
    var selectedRequiredCount = visibleRequiredOptions.filter(function(opt) {
      return self.selectedOptions.has(opt.id);
    }).length;
    var isRequiredComplete = selectedRequiredCount === requiredCount;

    var requiredSelections = visibleRequiredOptions
      .filter(function(opt) { return self.selectedOptions.has(opt.id); })
      .map(function(opt) {
        var selection = self.selectedOptions.get(opt.id);
        var displayText = selection.value;
        if (selection.customSize && selection.width && selection.height) {
          displayText += ' (' + selection.width + '×' + selection.height + 'mm)';
        }
        return displayText;
      })
      .join('・');

    var visibleOptionalOptions = this.optionalOptions.filter(function(opt) {
      return Visibility.isOptionVisible(opt, rule);
    });
    var optionalCount = visibleOptionalOptions.length;
    var selectedOptionalCount = visibleOptionalOptions.filter(function(opt) {
      return self.selectedOptions.has(opt.id);
    }).length;

    var optionalSelections = visibleOptionalOptions
      .filter(function(opt) { return self.selectedOptions.has(opt.id); })
      .map(function(opt) {
        var selection = self.selectedOptions.get(opt.id);
        return selection.value;
      })
      .join('・');

    var params = {
      requiredCount: requiredCount,
      selectedRequiredCount: selectedRequiredCount,
      isRequiredComplete: isRequiredComplete,
      requiredSelections: requiredSelections,
      optionalCount: optionalCount,
      selectedOptionalCount: selectedOptionalCount,
      optionalSelections: optionalSelections
    };

    if (container) {
      container.innerHTML = UIGenerator.generateSidebarButtonsPC(params);
    }

    if (mobileContainer) {
      mobileContainer.innerHTML = UIGenerator.generateSidebarButtonsMobile(params);
      this.attachMobileEventListeners();
    }
  };

  // =========================================
  // イベントリスナー
  // =========================================

  GroupedOptionsManager.prototype.attachEventListeners = function() {
    var self = this;
    
    var overlay = document.getElementById('grouped-options-overlay');
    if (overlay) {
      overlay.addEventListener('click', function(e) {
        if (e.target.id === 'grouped-options-overlay') {
          self.cancelSelection();
        }
        if (e.target.id === 'grouped-options-close' || e.target.id === 'grouped-options-cancel') {
          self.cancelSelection();
        }
        if (e.target.id === 'grouped-options-confirm') {
          self.confirmSelection();
        }
      });
    }

    var categoryList = document.getElementById('options-category-list');
    if (categoryList) {
      categoryList.addEventListener('click', function(e) {
        var categoryItem = e.target.closest('.options-category-item');
        if (categoryItem) {
          self.selectCategory(categoryItem.dataset.optionId);
        }
      });
    }

    var detailPanel = document.getElementById('options-detail-panel');
    if (detailPanel) {
      detailPanel.addEventListener('change', function(e) {
        if (e.target.name && e.target.name.startsWith('grouped-option-')) {
          self.handleChoiceChange(e.target);
        }
      });
      
      detailPanel.addEventListener('click', function(e) {
        var clearBtn = e.target.closest('.clear-option-btn');
        if (clearBtn) {
          self.clearOptionSelection(clearBtn.dataset.optionId);
        }
        
        // 分納：配送先追加ボタン
        var addBtn = e.target.closest('.split-delivery-add-btn');
        if (addBtn) {
          var container = addBtn.closest('.split-delivery-container');
          if (container) {
            self.addSplitDeliveryItem(container.dataset.optionId);
          }
        }
        
        // 分納：配送先削除ボタン
        var removeBtn = e.target.closest('.split-delivery-remove-btn');
        if (removeBtn) {
          var item = removeBtn.closest('.split-delivery-item');
          var container = removeBtn.closest('.split-delivery-container');
          if (item && container) {
            self.removeSplitDeliveryItem(container.dataset.optionId, parseInt(removeBtn.dataset.index));
          }
        }
      });
      
      detailPanel.addEventListener('input', function(e) {
        // カスタムサイズ入力
        if (e.target.id && (e.target.id.startsWith('custom-width-') || e.target.id.startsWith('custom-height-'))) {
          var optionId = e.target.id.replace('custom-width-', '').replace('custom-height-', '');
          var currentSelection = self.selectedOptions.get(optionId);
          if (currentSelection && currentSelection.customSize) {
            var widthInput = document.getElementById('custom-width-' + optionId);
            var heightInput = document.getElementById('custom-height-' + optionId);
            
            self.selectedOptions.set(optionId, {
              ...currentSelection,
              width: widthInput ? widthInput.value : '',
              height: heightInput ? heightInput.value : ''
            });
          }
        }
        
        // 追加入力フィールド
        if (e.target.id && e.target.id.startsWith('additional-field-')) {
          var parts = e.target.id.replace('additional-field-', '').split('-');
          var optionId = parts[0];
          var currentSelection = self.selectedOptions.get(optionId);
          var option = self.allOptions.find(function(opt) { return opt.id === optionId; });
          
          if (currentSelection && option && option.additionalFields) {
            currentSelection.additionalFieldValues = self.getAdditionalFieldValues(optionId, option.additionalFields);
            self.selectedOptions.set(optionId, currentSelection);
          }
        }
        
        // 分納フォームの入力
        if (e.target.classList.contains('split-delivery-input') || e.target.classList.contains('split-delivery-select') || e.target.classList.contains('split-delivery-textarea')) {
          var container = e.target.closest('.split-delivery-container');
          if (container) {
            self.updateSplitDeliverySummary(container.dataset.optionId);
          }
        }
      });
      
      // 分納：荷主情報チェックボックス
      detailPanel.addEventListener('change', function(e) {
        if (e.target.classList.contains('split-delivery-sender-same-checkbox')) {
          self.toggleSenderFields(e.target);
        }
      });
    }
    
    var optionsContainer = document.getElementById('product-options-container');
    if (optionsContainer) {
      optionsContainer.addEventListener('click', function(e) {
        var requiredButton = e.target.closest('#required-options-button');
        var optionalButton = e.target.closest('#optional-options-button');
        
        if (requiredButton) {
          self.openModal('required');
        } else if (optionalButton) {
          self.openModal('optional');
        }
      });
    }
    
    // 部数変更時の分納データ警告
    this.attachQuantityChangeListener();
  };
  
  /**
   * 部数変更時に分納データがある場合の警告機能
   */
  GroupedOptionsManager.prototype.attachQuantityChangeListener = function() {
    var self = this;
    var lastQuantity = null;
    
    // 部数変更を監視するセレクターをすべて取得
    var quantitySelectors = [
      '#quantity-input',
      '#quantity-select',
      '#mobile-quantity-select',
      '.goods-select-quantity select'
    ];
    
    quantitySelectors.forEach(function(selector) {
      document.querySelectorAll(selector).forEach(function(el) {
        el.addEventListener('change', function(e) {
          var newQuantity = parseInt(e.target.value) || 0;
          self.checkQuantityChangeForSplitDelivery(newQuantity);
        });
      });
    });
    
    // 価格表のセルクリック時も監視
    document.addEventListener('click', function(e) {
      var priceCell = e.target.closest('.price-cell, .paginated-price-cell, [data-quantity]');
      if (priceCell) {
        // 少し遅延させて数量が更新された後にチェック
        setTimeout(function() {
          if (window.priceCalculator && window.priceCalculator.currentSelection) {
            var newQuantity = window.priceCalculator.currentSelection.quantity;
            self.checkQuantityChangeForSplitDelivery(newQuantity);
          }
        }, 100);
      }
    });
  };
  
  /**
   * 分納データがある場合に部数変更を検出して警告
   * @param {number} newQuantity - 新しい数量
   */
  GroupedOptionsManager.prototype.checkQuantityChangeForSplitDelivery = function(newQuantity) {
    var self = this;
    
    // 分納指定オプションを探す
    var hasSplitDelivery = false;
    var splitDeliveryData = null;
    
    this.selectedOptions.forEach(function(selection, optionId) {
      if (selection.additionalFieldValues && selection.additionalFieldValues.split_delivery_data) {
        hasSplitDelivery = true;
        try {
          var dataStr = selection.additionalFieldValues.split_delivery_data;
          splitDeliveryData = typeof dataStr === 'string' ? JSON.parse(dataStr) : dataStr;
        } catch (e) {
          console.error('[GroupedOptions] 分納データ解析エラー:', e);
        }
      }
    });
    
    if (!hasSplitDelivery || !splitDeliveryData) {
      return;
    }
    
    // 分納データの合計数量
    var totalSplitQuantity = splitDeliveryData.total_quantity || 0;
    
    // 数量が変更され、分納データの合計と一致しない場合に警告
    if (totalSplitQuantity > 0 && totalSplitQuantity !== newQuantity) {
      // 一度だけ警告を表示（連続クリック防止）
      if (!this._splitDeliveryWarningShown || this._lastWarningQuantity !== newQuantity) {
        this._splitDeliveryWarningShown = true;
        this._lastWarningQuantity = newQuantity;
        
        var message = '注文数量が変更されました。\n\n' +
          '【現在の注文数量】' + newQuantity.toLocaleString() + '部\n' +
          '【分納先の合計数量】' + totalSplitQuantity.toLocaleString() + '部\n\n' +
          '分納先の数量を調整する必要があります。\n' +
          '「任意オプション」から分納指定の内容を修正してください。';
        
        alert(message);
        
        // 5秒後に警告フラグをリセット
        setTimeout(function() {
          self._splitDeliveryWarningShown = false;
          self._lastWarningQuantity = null;
        }, 5000);
      }
    }
  };

  GroupedOptionsManager.prototype.attachMobileEventListeners = function() {
    var self = this;
    
    var mobileRequiredBtn = document.getElementById('mobile-required-options-button');
    var mobileOptionalBtn = document.getElementById('mobile-optional-options-button');
    
    if (mobileRequiredBtn) {
      mobileRequiredBtn.addEventListener('click', function() {
        Mobile.openMobileSlidePanel(self, 'required');
      });
    }
    
    if (mobileOptionalBtn) {
      mobileOptionalBtn.addEventListener('click', function() {
        Mobile.openMobileSlidePanel(self, 'optional');
      });
    }
  };

  // =========================================
  // モバイル用（委譲）
  // =========================================

  GroupedOptionsManager.prototype.openMobileSlidePanel = function(optionType) {
    Mobile.openMobileSlidePanel(this, optionType);
  };

  // =========================================
  // ユーティリティ（外部公開用）
  // =========================================

  GroupedOptionsManager.prototype.getSelectedOptions = function() {
    return this.selectedOptions;
  };

  GroupedOptionsManager.prototype.isComplete = function() {
    var self = this;
    return this.requiredOptions.every(function(opt) {
      return self.selectedOptions.has(opt.id);
    });
  };

  // ユーティリティメソッド（互換性のため残す）
  GroupedOptionsManager.prototype.normalizeChoice = function(choice) {
    return Utils.normalizeChoice(choice);
  };

  GroupedOptionsManager.prototype.escapeHtml = function(text) {
    return Utils.escapeHtml(text);
  };

  GroupedOptionsManager.prototype.sanitizeChoiceLabel = function(text) {
    return Utils.sanitizeChoiceLabel(text);
  };

  GroupedOptionsManager.prototype.processNewlines = function(text) {
    return Utils.processNewlines(text);
  };

  // グローバルに公開
  window.GroupedOptionsManager = GroupedOptionsManager;

})();
